import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import Axios from 'axios';
import { setupCache } from 'axios-cache-interceptor';
import { generateToken } from '@/helpers/user-helper';

let axios: AxiosInstance | null = null;
let axiosCached: AxiosInstance | null = null;

export function createAxios(options: Partial<AxiosRequestConfig<any>> = {}) {
	return Axios.create({ baseURL: '/', timeout: 20000, ...options });
}

export function getAxios(): AxiosInstance {
	if (!axios) {
		axios = createAxios();

		/**
		 * enforces `text/plain` for post
		 * @see:https://stackoverflow.com/questions/12320467/jquery-cors-content-type-options/12320736#12320736
		 */
		axios.interceptors.request.use((config: AxiosRequestConfig<any>) => {
			if (config.method?.toLowerCase() === 'post') {
				(config.headers ??= {})['Content-Type'] = 'text/plain';
			}
			return config;
		});

		axios.interceptors.request.use(async (config: AxiosRequestConfig<any>) => {
			const accessToken = config.headers?.['X-Access-Token'];

			// Clean up the signal header before sending
			delete config.headers?.['X-Access-Token'];

			if (!accessToken) return config;

			if (import.meta.server) {
				(config.headers ??= {})['Authorization'] = `Bearer ${accessToken}`;
			} else {
				const token = await generateToken();
				(config.headers ??= {})['Authorization'] = `Bearer ${token ?? accessToken}`;
			}

			return config;
		});
	}

	return axios;
}

function getAxiosServerCached(): AxiosInstance {
	if (!axiosCached) {
		axiosCached = setupCache(createAxios(), {
			methods: ['get', 'post'],
			ttl: 60 * 60 * 1000, // 1 hour
		});
	}

	return axiosCached;
}

export function getAxiosCached(): AxiosInstance {
	return import.meta.server ? getAxiosServerCached() : getAxios();
}
